import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import {
  Briefcase,
  Cpu,
  Banknote,
  Megaphone,
  Sparkles,
} from "lucide-react";

const CATEGORIES = [
  {
    num: "01",
    title: "Industry Operators",
    desc: "CEOs and MDs from manufacturing, logistics, and supply chain.",
    icon: Briefcase,
    accent: "#C7913A", // amber
    accentSoft: "#F5E2BC",
    count: 14,
    roster: ["Lotus Auto", "Fortune City", "Revamp Moto"],
  },
  {
    num: "02",
    title: "Tech Builders",
    desc: "CTOs and product leaders from category-defining companies.",
    icon: Cpu,
    accent: "#6FA235", // lime forest
    accentSoft: "#DCE8C9",
    count: 12,
    roster: ["Ojesh AI", "BMSCE", "Christ University"],
  },
  {
    num: "03",
    title: "Finance & Capital",
    desc: "CAs, investment bankers, and CFOs who've closed rounds.",
    icon: Banknote,
    accent: "#14401F", // deep forest
    accentSoft: "#CDDABF",
    count: 13,
    roster: ["Jumbotail", "ABInBev", "Wells Fargo"],
  },
  {
    num: "04",
    title: "GTM & Growth",
    desc: "Sales, marketing, and growth ops who scaled from 0 to 1.",
    icon: Megaphone,
    accent: "#B8431F", // brick red
    accentSoft: "#F5C9B6",
    count: 11,
    roster: ["We Founder Circle", "IndiaMART", "AY Ventures"],
  },
];

// Companies our operators come from (scrolling roster)
const ROSTER_MARQUEE = [
  "Lotus Auto",
  "Jumbotail",
  "ABInBev",
  "Wells Fargo",
  "IndiaMART",
  "We Founder Circle",
  "Revamp Moto",
  "Fortune City",
  "AY Ventures",
  "IIM Indore",
  "BMSCE",
  "Ojesh AI",
  "Christ University",
];

// Animated counter
function Counter({ to, duration = 1.6, suffix = "+" }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // ease out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(eased * to));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {v}
      {suffix}
    </span>
  );
}

export default function MentorNetwork() {
  return (
    <section
      id="platform"
      data-testid="mentor-section"
      className="bg-[#F7F8EF] relative overflow-hidden"
    >
      {/* Decorative subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(#14401F 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* Amber glow blob */}
      <div
        className="absolute -left-32 top-1/3 w-[460px] h-[460px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,145,58,0.14), transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Header row: title + giant counter */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-20">
          <div className="max-w-3xl">
            <Eyebrow>Mentor Network · The Roster</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-bold text-[#0F2818]">
              Backed by 50+ Operators.
              <br />
              Zero Egos. <Italic className="text-[#C7913A]">Infinite Grit</Italic>.
            </h2>
            <p className="mt-6 text-[#4A5A4F] max-w-xl text-sm leading-relaxed">
              We don&apos;t just write cheques. Every founder gets matched to
              4 hand-picked operators who&apos;ve already built, sold, scaled,
              or shipped — across four distinct disciplines.
            </p>
          </div>

          {/* Animated big counter */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="shrink-0 relative"
          >
            <div className="relative inline-flex items-baseline gap-2 px-7 py-5 rounded-3xl border border-[#14401F]/12 bg-white/70 backdrop-blur-sm shadow-[0_4px_20px_-12px_rgba(20,64,31,0.18)]">
              {/* Pulsing dot top-right */}
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B8D958] opacity-70" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#B8D958]" />
              </span>
              <span className="text-6xl lg:text-7xl font-serif-display font-bold text-[#14401F] leading-none tabular-nums tracking-tight">
                <Counter to={50} suffix="+" />
              </span>
              <span className="flex flex-col items-start">
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#14401F]/60">
                  Operator
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#14401F]/60">
                  Mentors
                </span>
              </span>
            </div>
          </motion.div>
        </div>

        {/* Connector SVG overlay (visible on lg+) */}
        <div className="relative">
          <svg
            className="hidden lg:block absolute inset-0 w-full h-full pointer-events-none z-0"
            viewBox="0 0 100 30"
            preserveAspectRatio="none"
            aria-hidden
          >
            <motion.path
              d="M 12 12 C 25 4, 40 22, 50 12 S 75 4, 88 18"
              stroke="#14401F"
              strokeOpacity="0.18"
              strokeWidth="0.25"
              strokeDasharray="0.6 0.8"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.6, ease: "easeOut" }}
            />
          </svg>

          {/* Staggered grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative z-10">
            {CATEGORIES.map((c, i) => (
              <OperatorCard key={c.num} c={c} idx={i} />
            ))}
          </div>
        </div>

        {/* Roster marquee bottom strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 lg:mt-20 relative"
        >
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#C7913A]" />
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#14401F]/60">
              Where our operators come from
            </span>
            <div className="flex-1 h-px bg-[#14401F]/10" />
          </div>
          <div className="overflow-hidden relative">
            {/* Fade gradients on sides */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#F7F8EF] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#F7F8EF] to-transparent z-10 pointer-events-none" />

            <div className="flex gap-3 mn-marquee py-2">
              {[...ROSTER_MARQUEE, ...ROSTER_MARQUEE].map((name, i) => (
                <span
                  key={`${name}-${i}`}
                  className="shrink-0 px-4 py-1.5 rounded-full bg-white border border-[#14401F]/10 text-xs font-medium text-[#14401F]/85 font-mono whitespace-nowrap"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Marquee animation */}
      <style>{`
        @keyframes mn-marquee-move {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .mn-marquee {
          animation: mn-marquee-move 38s linear infinite;
          width: max-content;
        }
        .mn-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}

function OperatorCard({ c, idx }) {
  const Icon = c.icon;
  // Stagger vertically for desktop (zig-zag) — 1st and 3rd raised, 2nd and 4th lowered
  const yOffsetClass = idx % 2 === 0 ? "lg:translate-y-0" : "lg:translate-y-6";

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: idx * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`mentor-category-${idx}`}
      className={`group relative transform transition-transform ${yOffsetClass}`}
    >
      <div
        className="relative h-full rounded-[28px] bg-white p-6 lg:p-7 border border-[#14401F]/10 overflow-hidden transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_18px_40px_-20px_rgba(20,64,31,0.25)]"
        style={{ minHeight: "340px" }}
      >
        {/* Top brand stripe (color per category) */}
        <div
          className="absolute top-0 left-0 right-0 h-1.5 transition-all duration-300 group-hover:h-2"
          style={{ background: c.accent }}
        />
        {/* Soft brand blob bottom-right */}
        <div
          className="absolute -right-10 -bottom-10 w-44 h-44 rounded-full opacity-0 group-hover:opacity-70 transition-opacity duration-500"
          style={{
            background: `radial-gradient(closest-side, ${c.accentSoft}, transparent 70%)`,
          }}
        />

        {/* Number chip top-right */}
        <div className="flex items-start justify-between mb-6">
          <div
            className="relative w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6"
            style={{
              backgroundColor: c.accentSoft,
              color: c.accent,
            }}
          >
            <Icon className="h-6 w-6" strokeWidth={1.8} />
            {/* Rotating ring (decorative) */}
            <svg
              className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity"
              viewBox="0 0 56 56"
              aria-hidden
            >
              <circle
                cx="28"
                cy="28"
                r="26"
                fill="none"
                stroke={c.accent}
                strokeOpacity="0.35"
                strokeWidth="1"
                strokeDasharray="3 4"
                className="mn-spin"
                style={{ transformOrigin: "28px 28px" }}
              />
            </svg>
          </div>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold"
            style={{ color: c.accent }}
          >
            {c.num}
          </span>
        </div>

        {/* Title + desc */}
        <h3 className="font-serif-display text-xl lg:text-[22px] font-bold text-[#0F2818] mb-2 leading-tight">
          {c.title}
        </h3>
        <p className="text-sm text-[#4A5A4F] leading-relaxed mb-5">
          {c.desc}
        </p>

        {/* Roster sample chips */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {c.roster.map((r) => (
            <span
              key={r}
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md text-[#14401F]/75 border border-[#14401F]/10 bg-[#F7F8EF]"
            >
              {r}
            </span>
          ))}
        </div>

        {/* Bottom stat row */}
        <div className="absolute left-6 right-6 bottom-5 lg:left-7 lg:right-7 lg:bottom-6">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[9px] uppercase tracking-[0.22em] font-semibold text-[#14401F]/50">
              Mentors in class
            </span>
            <span
              className="text-sm font-mono font-bold tabular-nums"
              style={{ color: c.accent }}
            >
              {c.count}
            </span>
          </div>
          <div className="h-1 rounded-full bg-[#14401F]/8 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${Math.min(100, (c.count / 15) * 100)}%` }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 1.1, delay: 0.3 + idx * 0.12, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: c.accent }}
            />
          </div>
        </div>
      </div>

      {/* Local CSS for spin */}
      <style>{`
        @keyframes mn-spin-anim {
          from { transform: rotate(0); }
          to { transform: rotate(360deg); }
        }
        .mn-spin {
          animation: mn-spin-anim 14s linear infinite;
        }
      `}</style>
    </motion.div>
  );
}
